import {
  createGraphQLFailureAction,
  createGraphQLRequestAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import {
  DetectionMoreResultsResponse,
  InLoadMediaRequest,
  SaveDataRequest,
  SaveDetectionUserEditedRequest,
  SaveMediaDataRequest,
  TrackingMoreResultsResponse,
  TranscriptionServiceResponse,
} from '../models';
import {
  DeleteAssetsResponse,
  FetchCorsightResultsResponse,
  FetchDownloadAssetStatusResponse,
  FetchJobsStatusesResponse,
  LoadListAssetResponse,
  LoadMediaDataResponse,
  LoadVoiceMaskAssetResponse,
  PollToDownloadResponse,
  SaveRedactionSelectionsResponse,
  UpdateMediaDetailsResponse,
  V3fJobResponse,
} from '../services';
import { createAction } from '@reduxjs/toolkit';
import {
  BulkAddBackgroundBlurRequest,
  BulkRedactRequest,
  ChangeCodeRequest,
  ChangeRedactionRequest,
  ChangeShapeRequest,
  ChangeTextOverlayRequest,
} from '@common-modules/mediaDetails';
import {
  createApiFailureAction,
  createApiRequestAction,
  createApiSuccessAction,
} from '@helpers/callApi';
import { DetectionEngineNormalizedData } from '../services/adapters/head-detection/models';
import { TDOId } from '@cbsa/state/modules/universal';
/**
 * Internal Actions.
 */

export const DISCOVER_DETECTION_GROUP_GAPS = createAction<void>(
  'vtn-redact-web-worker/DISCOVER_DETECTION_GROUP_GAPS'
);
export const actionDiscoverFaceClusterGaps = (payload?: void) =>
  DISCOVER_DETECTION_GROUP_GAPS(payload);

export const DOWNLOAD_ASSET_JOB_STATUS = createGraphQLRequestAction(
  'vtn-redact-web-worker/DOWNLOAD_ASSET_JOB_STATUS'
);
export const DOWNLOAD_ASSET_JOB_STATUS_SUCCESS = createGraphQLSuccessAction<
  FetchDownloadAssetStatusResponse,
  {
    readonly jobId: string;
  }
>('vtn-redact-web-worker/DOWNLOAD_ASSET_JOB_STATUS_SUCCESS');
export const DOWNLOAD_ASSET_JOB_STATUS_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/DOWNLOAD_ASSET_JOB_STATUS_FAILURE'
);

export const POLL_DOWNLOAD_ASSET = 'vtn-redact-web-worker/POLL_DOWNLOAD_ASSET';
export const POLL_DOWNLOAD_ASSET_SUCCESS =
  createGraphQLSuccessAction<PollToDownloadResponse>(
    'vtn-redact-web-worker/POLL_DOWNLOAD_ASSET_SUCCESS'
  );
export const POLL_DOWNLOAD_ASSET_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/POLL_DOWNLOAD_ASSET_FAILURE'
);

export const FETCH_DOWNLOAD_ASSET = createGraphQLRequestAction(
  'vtn-redact-web-worker/FETCH_DOWNLOAD_ASSET'
);
// export const FETCH_DOWNLOAD_ASSET_SUCCESS =
//   'vtn-redact-web-worker/FETCH_DOWNLOAD_ASSET_SUCCESS';
export const FETCH_DOWNLOAD_ASSET_FAILURE = createGraphQLFailureAction<
  any,
  { id: TDOId; tdoName: string },
  true
>('vtn-redact-web-worker/FETCH_DOWNLOAD_ASSET_FAILURE');

export const FETCH_REDACT_TDO = 'vtn-redact-web-worker/FETCH_REDACT_TDO';
export const FETCH_REDACT_TDO_SUCCESS =
  createGraphQLSuccessAction<V3fJobResponse>(
    'vtn-redact-web-worker/FETCH_REDACT_TDO_SUCCESS' // Mark Return Worker
  );
export const FETCH_REDACT_TDO_FAILURE = createGraphQLFailureAction<
  any,
  { id: TDOId; tdoName: string; redactEngineID: string },
  true
>('vtn-redact-web-worker/FETCH_REDACT_TDO_FAILURE');

// ETODO these might be deprecated!
export const LOAD_MEDIA_DATA = createAction<InLoadMediaRequest>(
  'vtn-redact-web-worker/LOAD_MEDIA_DATA'
);
export const actionLoadMediaData = (payload: InLoadMediaRequest) =>
  LOAD_MEDIA_DATA(payload);

export const LOAD_MEDIA_DATA_SUCCESS = createGraphQLSuccessAction<
  LoadMediaDataResponse,
  { id: TDOId },
  true
>('vtn-redact-web-worker/LOAD_MEDIA_DATA_SUCCESS');
export const LOAD_MEDIA_DATA_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/LOAD_MEDIA_DATA_FAILURE'
);
// TODO: Fix the type
// export const LOAD_MEDIA_DATA_ASSET_SUCCESS = createAction(
//   'vtn-redact-web-worker/LOAD_MEDIA_DATA_ASSET_SUCCESS',
//   (payload: SaveMediaDataRequest, meta?: unknown) => ({
//     payload,
//     meta,
//   })
// );
export const LOAD_MEDIA_DATA_ASSET_SUCCESS =
  createApiSuccessAction<SaveMediaDataRequest>(
    'vtn-redact-web-worker/LOAD_MEDIA_DATA_ASSET_SUCCESS'
  );

export const LOAD_MEDIA_DATA_ASSET_FAILURE = createApiFailureAction(
  'vtn-redact-web-worker/LOAD_MEDIA_DATA_ASSET_FAILURE'
);

export const SAVE_MEDIA_DATA = createAction<SaveDataRequest>(
  'vtn-redact-web-worker/SAVE_MEDIA_DATA'
);
export const actionSaveMediaData = (payload: SaveDataRequest) =>
  SAVE_MEDIA_DATA(payload);

export interface SaveMediaDataSuccessPayload {
  createAsset: {
    id: string;
    createdDateTime: string;
    /** VE-25870: the counters this save published, for the staleness check. */
    details?: Record<string, unknown>;
    /** VE-25870: gates the legacy timestamp fallback; see the staleness selector. */
    fileData?: { size?: number | null };
    container: {
      id: string;
      modifiedDateTime: string;
    };
  };
}
export interface SaveMediaDataSuccessMeta {
  readonly variables: Record<string, unknown>;
  readonly operationName?: string;
  readonly query?: string;
  /**
   * VE-27765: the editor's `saveRev` when this save was requested. The main
   * thread only calls the editor clean if no edit has bumped the rev since.
   */
  readonly savedRev?: number;
}
export const SAVE_MEDIA_DATA_SUCCESS = createAction(
  'vtn-redact-web-worker/SAVE_MEDIA_DATA_SUCCESS',
  (
    payload: SaveMediaDataSuccessPayload,
    meta: SaveMediaDataSuccessMeta,
    error?: false
  ) => ({ payload, meta, error })
);
export const SAVE_MEDIA_DATA_FAILURE = createApiFailureAction(
  'vtn-redact-web-worker/SAVE_MEDIA_DATA_FAILURE'
);

export const SET_ASSET_CHECKPOINT = createAction<{ checkpoint: number }>(
  'vtn-redact-web-worker/SET_ASSET_CHECKPOINT'
);

export const actionSetAssetCheckpoint = (payload: { checkpoint: number }) =>
  SET_ASSET_CHECKPOINT(payload);

export const SAVE_REDACTED_TRANSCRIPT = createAction<SaveDataRequest>(
  'vtn-redact-web-worker/SAVE_REDACTED_TRANSCRIPT'
);

export const actionSaveRedactedTranscript = (payload: SaveDataRequest) =>
  SAVE_REDACTED_TRANSCRIPT(payload);

export const SAVE_REDACTED_TRANSCRIPT_SUCCESS = createApiSuccessAction(
  'vtn-redact-web-worker/SAVE_REDACTED_TRANSCRIPT_SUCCESS'
);

export const SAVE_REDACTED_TRANSCRIPT_FAILURE = createApiFailureAction(
  'vtn-redact-web-worker/SAVE_REDACTED_TRANSCRIPT_FAILURE'
);

/** Dispatched with the whole redact request, so it carries `assetVersion` too. */
export const SAVE_REDACTION_SELECTIONS = createAction<
  SaveDataRequest & { readonly assetVersion?: number }
>('vtn-redact-web-worker/SAVE_REDACTION_SELECTIONS');
export const actionSaveRedactionSelections = (
  payload: SaveDataRequest & { readonly assetVersion?: number }
) => SAVE_REDACTION_SELECTIONS(payload);

export const SAVE_REDACTION_SELECTIONS_SUCCESS =
  createApiSuccessAction<SaveRedactionSelectionsResponse>(
    'vtn-redact-web-worker/SAVE_REDACTION_SELECTIONS_SUCCESS'
  );
export const SAVE_REDACTION_SELECTIONS_FAILURE = createApiFailureAction(
  'vtn-redact-web-worker/SAVE_REDACTION_SELECTIONS_FAILURE'
);

// This action is not handled anywhere
export const UPDATE_MEDIA_DETAILS = createGraphQLRequestAction(
  'vtn-redact-web-worker/UPDATE_MEDIA_DETAILS'
);

// TODO: I believe this was unused, but leaving it commented out for now
// export const actionUpdateMediaDetails = (payload: UpdateMediaDetailsRequest) =>
//   UPDATE_MEDIA_DETAILS(payload);

export const UPDATE_MEDIA_DETAILS_SUCCESS =
  createGraphQLSuccessAction<UpdateMediaDetailsResponse>(
    'vtn-redact-web-worker/UPDATE_MEDIA_DETAILS_SUCCESS'
  );
export const UPDATE_MEDIA_DETAILS_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/UPDATE_MEDIA_DETAILS_FAILURE'
);

// Engine Results
// export const FETCH_DETECTION_ENGINE_RESULTS =
//   createAction<FetchEngineResultsRequest>(
//     'vtn-redact-web-worker/FETCH_DETECTION_ENGINE_RESULTS'
//   );
export const FETCH_DETECTION_ENGINE_RESULTS = createApiRequestAction(
  'vtn-redact-web-worker/FETCH_DETECTION_ENGINE_RESULTS'
);
export const FETCH_DETECTION_ENGINE_RESULTS_SUCCESS =
  createApiSuccessAction<DetectionEngineNormalizedData>(
    'vtn-redact-web-worker/FETCH_DETECTION_ENGINE_RESULTS_SUCCESS'
  );
export const FETCH_DETECTION_MORE_ENGINE_RESULTS_SUCCESS =
  createAction<DetectionMoreResultsResponse>(
    'vtn-redact-web-worker/FETCH_DETECTION_MORE_ENGINE_RESULTS_SUCCESS'
  );
export const FETCH_DETECTION_ENGINE_RESULTS_FAILURE = createApiFailureAction(
  'vtn-redact-web-worker/FETCH_DETECTION_ENGINE_RESULTS_FAILURE'
);

export const actionDetectionMoreResultsSuccess = (
  payload: DetectionMoreResultsResponse
) => FETCH_DETECTION_MORE_ENGINE_RESULTS_SUCCESS(payload);

export const SAVE_DETECTION_ENGINE_RESULTS =
  createAction<SaveDetectionUserEditedRequest>(
    'vtn-redact-web-worker/SAVE_DETECTION_ENGINE_RESULTS'
  );
export const SAVE_DETECTION_ENGINE_RESULTS_SUCCESS = createAction<any>(
  'vtn-redact-web-worker/SAVE_DETECTION_ENGINE_RESULTS_SUCCESS'
);
export const SAVE_DETECTION_ENGINE_RESULTS_FAILURE = createAction<any>(
  'vtn-redact-web-worker/SAVE_DETECTION_ENGINE_RESULTS_FAILURE'
);

export const SAVE_DETECTION_ENGINE_RESULTS_FOR_TRIM =
  'vtn-redact-web-worker/SAVE_DETECTION_ENGINE_FOR_TRIM_RESULTS';
export const SAVE_DETECTION_ENGINE_RESULTS_FOR_TRIM_SUCCESS =
  'vtn-redact-web-worker/SAVE_DETECTION_ENGINE_RESULTS_FOR_TRIM_SUCCESS';
export const SAVE_DETECTION_ENGINE_RESULTS_FOR_TRIM_FAILURE =
  'vtn-redact-web-worker/SAVE_DETECTION_ENGINE_RESULTS_FOR_TRIM_FAILURE';

export const actionDetectionSaveResults = (
  payload: SaveDetectionUserEditedRequest
) => SAVE_DETECTION_ENGINE_RESULTS(payload);

export const FETCH_TRACKING_ENGINE_RESULTS =
  'vtn-redact-web-worker/FETCH_TRACKING_ENGINE_RESULTS';
export const FETCH_TRACKING_ENGINE_RESULTS_SUCCESS =
  'vtn-redact-web-worker/FETCH_TRACKING_ENGINE_RESULTS_SUCCESS';
export const FETCH_TRACKING_MORE_ENGINE_RESULTS_SUCCESS = createAction(
  'vtn-redact-web-worker/FETCH_TRACKING_MORE_ENGINE_RESULTS_SUCCESS',
  (payload: TrackingMoreResultsResponse, meta?: unknown) => ({
    payload,
    meta,
  })
);
export const FETCH_TRACKING_ENGINE_RESULTS_FAILURE =
  'vtn-redact-web-worker/FETCH_TRACKING_ENGINE_RESULTS_FAILURE';

export const actionTrackingMoreResultsSuccess = (
  payload: TrackingMoreResultsResponse
) => FETCH_TRACKING_MORE_ENGINE_RESULTS_SUCCESS(payload);

export const FETCH_TRANSCRIPTION_RESULTS =
  'vtn-redact-web-worker/FETCH_TRANSCRIPTION_RESULTS';
export const FETCH_TRANSCRIPTION_RESULTS_SUCCESS =
  createGraphQLSuccessAction<TranscriptionServiceResponse>(
    'vtn-redact-web-worker/FETCH_TRANSCRIPTION_RESULTS_SUCCESS'
  );
export const FETCH_TRANSCRIPTION_RESULTS_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/FETCH_TRANSCRIPTION_RESULTS_FAILURE'
);

export const FETCH_SPEAKER_SEPARATION_RESULTS =
  'vtn-redact-web-worker/FETCH_SPEAKER_SEPARATION_RESULTS';
export const FETCH_SPEAKER_SEPARATION_RESULTS_SUCCESS =
  createGraphQLSuccessAction<TranscriptionServiceResponse>(
    'vtn-redact-web-worker/FETCH_SPEAKER_SEPARATION_RESULTS_SUCCESS'
  );
export const FETCH_SPEAKER_SEPARATION_RESULTS_FAILURE =
  createGraphQLFailureAction(
    'vtn-redact-web-worker/FETCH_SPEAKER_SEPARATION_RESULTS_FAILURE'
  );

export const SAVE_TRANSCRIPTION_RESULTS_FOR_TRIM =
  'vtn-redact-web-worker/SAVE_TRANSCRIPTION_RESULTS_FOR_TRIM';
export const SAVE_TRANSCRIPTION_RESULTS_FOR_TRIM_SUCCESS =
  'vtn-redact-web-worker/SAVE_TRANSCRIPTION_RESULTS_FOR_TRIM_SUCCESS';
export const SAVE_TRANSCRIPTION_RESULTS_FOR_TRIM_FAILURE =
  'vtn-redact-web-worker/SAVE_TRANSCRIPTION_RESULTS_FOR_TRIM_FAILURE';

export const FETCH_ASSET_RESULTS_SUCCESS = createGraphQLSuccessAction<
  LoadListAssetResponse,
  { assetTypes: readonly string[] }
>('vtn-redact-web-worker/FETCH_ASSET_RESULTS_SUCCESS');
export const FETCH_ASSET_RESULTS_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/FETCH_ASSET_RESULTS_FAILURE'
);

export const DELETE_ASSET_SUCCESS =
  createGraphQLSuccessAction<DeleteAssetsResponse>(
    'vtn-redact-web-worker/DELETE_ASSET_SUCCESS'
  );
export const DELETE_ASSET_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/DELETE_ASSET_FAILURE'
);

export const FETCH_JOBS_STATUS = 'vtn-redact-web-worker/FETCH_JOB_STATUS';
export const FETCH_JOBS_STATUS_SUCCESS =
  createGraphQLSuccessAction<FetchJobsStatusesResponse>(
    'vtn-redact-web-worker/FETCH_JOB_STATUS_SUCCESS'
  );
export const FETCH_JOBS_STATUS_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/FETCH_JOB_STATUS_FAILURE'
);

export const FETCH_CORSIGHT_RESULTS =
  'vtn-redact-web-worker/FETCH_CORSIGHT_RESULTS';
export const FETCH_CORSIGHT_RESULTS_SUCCESS =
  createGraphQLSuccessAction<FetchCorsightResultsResponse>(
    'vtn-redact-web-worker/FETCH_CORSIGHT_RESULTS_SUCCESS'
  );
export const FETCH_CORSIGHT_RESULTS_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/FETCH_CORSIGHT_RESULTS_FAILURE'
);

export const CHANGE_SHAPE = createAction<ChangeShapeRequest>(
  'vtn-redact-web-worker/CHANGE_SHAPE'
);

export const CHANGE_CODE = createAction<ChangeCodeRequest>(
  'vtn-redact-web-worker/CHANGE_CODE'
);

export const CHANGE_TEXT_OVERLAY = createAction<ChangeTextOverlayRequest>(
  'vtn-redact-web-worker/CHANGE_TEXT_OVERLAY'
);

export const SET_DETECTION_TYPE_REDACTION = createAction<BulkRedactRequest>(
  'vtn-redact-web-worker/SET_DETECTION_TYPE_REDACTION'
);

export const BULK_ADD_BACKGROUND_BLUR =
  createAction<BulkAddBackgroundBlurRequest>(
    'vtn-redact-web-worker/BULK_ADD_BACKGROUND_BLUR'
  );

export const SET_INDIVIDUAL_REDACTION = createAction<ChangeRedactionRequest>(
  'vtn-redact-web-worker/SET_INDIVIDUAL_REDACTION'
);

export const LOAD_VOICE_MASK_ASSET_SUCCESS = createGraphQLSuccessAction<
  LoadVoiceMaskAssetResponse,
  { id: TDOId },
  true
>('vtn-redact-web-worker/LOAD_VOICE_MASK_ASSET_SUCCESS');
export const LOAD_VOICE_MASK_ASSET_FAILURE = createGraphQLFailureAction(
  'vtn-redact-web-worker/LOAD_VOICE_MASK_ASSET_FAILURE'
);
